import { create } from 'zustand';
import { clearAuth, getAuthData, isAuthenticated as checkAuth } from '../services/zklogin-auth';
import { clearSupabaseSession, restoreSupabaseSession } from '../services/supabase-auth';
import { clearSessionId, getSessionId } from '../services/api-client';
import { registerPushSubscription } from '../services/push-subscription';

async function endServerSession(): Promise<void> {
  const sessionId = getSessionId();
  if (!sessionId) return;
  try {
    await fetch('/api/auth/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionId}` },
      body: JSON.stringify({ action: 'logout' }),
    });
  } catch { /* non-fatal */ }
}

interface AuthState {
  isAuthenticated: boolean;
  userAddress: string | null;
  jwt: string | null;
  ephemeralKeypair: any | null;
  zkProof: string | null;

  // Actions
  setAuth: (jwt: string, userAddress: string, ephemeralKeypair: any, zkProof: string) => void;
  logout: () => void;
  initialize: () => void;
}

// Synchronously check localStorage so the first render already knows auth state.
// This prevents a flash of the login screen and preserves deep-link URLs.
const initialAuth = checkAuth();
const initialData = initialAuth ? getAuthData() : null;

// Re-attach the persisted Supabase JWT for RLS — fire-and-forget, runs in parallel with first render.
if (initialData?.address) {
  restoreSupabaseSession(initialData.address).catch(() => { /* non-fatal */ });
}

export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: initialAuth,
  userAddress: initialData?.address || null,
  jwt: initialData?.jwt || null,
  ephemeralKeypair: null,
  zkProof: initialData?.proof || null,

  setAuth: (jwt, userAddress, ephemeralKeypair, zkProof) => {
    // Store in localStorage (zklogin-auth.ts handles its own storage too)
    localStorage.setItem('coffee_rewards_jwt', jwt);
    localStorage.setItem('coffee_rewards_address', userAddress);
    if (zkProof) localStorage.setItem('coffee_rewards_proof', zkProof);

    set({
      isAuthenticated: true,
      jwt,
      userAddress,
      ephemeralKeypair,
      zkProof,
    });

    // Register PWA push subscription post-login. Uses the bearer sessionId
    // (set by zklogin-auth before setAuth fires). Non-blocking and silent on
    // failure — most failures are "user denied" or "no service worker support".
    try {
      const sid = getSessionId();
      if (sid) {
        registerPushSubscription(sid).catch((e) => console.warn('[push] register failed', e));
      }
    } catch { /* non-fatal */ }
  },

  logout: () => {
    endServerSession();
    clearAuth();
    clearSupabaseSession();
    clearSessionId();
    // Clear per-user selectedShop entries + the legacy global key
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && (k === 'bitbrew_selected_shop' || k.startsWith('bitbrew_selected_shop:'))) {
        localStorage.removeItem(k);
      }
    }
    set({ isAuthenticated: false, userAddress: null, jwt: null, ephemeralKeypair: null, zkProof: null });
  },

  initialize: () => {
    // Re-check localStorage (kept for backward compat; primary init is synchronous above)
    if (checkAuth()) {
      const authData = getAuthData();
      set({
        isAuthenticated: true,
        jwt: authData.jwt,
        userAddress: authData.address,
        zkProof: authData.proof,
        ephemeralKeypair: null,
      });
      if (authData.address) {
        restoreSupabaseSession(authData.address).catch(() => { /* non-fatal */ });
      }
    }
  },
}));
